import React, { useEffect, useState, useMemo } from "react";
import {
  MechanismsAPIResponse,
  mechanismsClient,
  mechanismsData,
} from "../../services/mechanismsClient";
import { PathwayTable } from "./PathwayTable";
import { PathwayGraph } from "./PathwayGraph";
import { GraphEvidenceTab } from "./GraphEvidenceTab";
import { LiteratureTab } from "./LiteratureTab";
import Snackbar from "@mui/material/Snackbar";
import Alert from "@mui/material/Alert";
import { Select, MenuItem, FormControl, InputLabel } from "@mui/material";
import { GeneTherapeuticHeader } from "../Header/GeneTherapeuticHeader";
import { useTheme } from "../../theme/useTheme";
import { formatDiseaseListForDisplay } from "../../utils/diseaseDisplayName";

export type Status =
  | "idle"
  | "loading"
  | "generating"
  | "queued"
  | "found"
  | "not_found"
  | "error";

export const STATUS: Record<Status, Status> = {
  idle: "idle",
  loading: "loading",
  generating: "generating",
  queued: "queued",
  found: "found",
  not_found: "not_found",
  error: "error",
};

/**
 * Builds the `disease` query param for mechanisms fetch/download.
 * When `allowSingleSelection` is true (current product default), only the first
 * label is sent so the API sees a single disease. When we later set
 * `allowSingleSelection` to false on the header, this passes the full
 * comma-separated string — confirm backend/storage supports that or adjust here
 * (e.g. multiple requests or a new contract).
 *
 * Values must stay as canonical taxonomy names (e.g. "diastolic heart failure");
 * use `formatDiseaseListForDisplay` only for UI copy.
 */
function resolveMechanismDiseaseParam(
  submittedDiseases: string | undefined,
  selectedDisease: string,
  allowSingleSelection: boolean,
): string | undefined {
  const raw =
    submittedDiseases && submittedDiseases.trim()
      ? submittedDiseases.trim()
      : selectedDisease && selectedDisease !== "all"
        ? selectedDisease
        : "";
  if (!raw) return undefined;
  if (allowSingleSelection) {
    const first = raw.split(",")[0]?.trim();
    return first || undefined;
  }
  return raw;
}

interface MechanismsProps {
  selectedGene: string | null;
  therapeuticArea: string | null;
  taxonomyNodes?: import("../../types/diseaseTaxonomy.types").DiseaseTaxonomyNode[];
  selectedDiseaseIds?: Set<number>;
  onDiseaseSelectionChange?: (ids: Set<number>) => void;
  selectedDiseases?: string;
  submittedDiseases?: string;
  onSubmitDiseases?: () => void;
  diseaseRefreshKey?: number;
  /**
   * Must stay in sync with `GeneTherapeuticHeader` / ontology controls.
   * `false` = multi-select UI + full comma-separated `disease` param (verify API).
   */
  allowSingleSelection?: boolean;
}

export const Mechanisms: React.FC<MechanismsProps> = ({
  selectedGene,
  therapeuticArea,
  taxonomyNodes,
  selectedDiseaseIds,
  onDiseaseSelectionChange,
  selectedDiseases,
  submittedDiseases,
  onSubmitDiseases,
  diseaseRefreshKey,
  allowSingleSelection = true,
}) => {
  const themeCtx = useTheme();
  const [mechanisms, setMechanisms] = useState<mechanismsData | null>(null);
  const [status, setStatus] = useState<Status>(STATUS.idle);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [selectedMechanismKey, setSelectedMechanismKey] = useState<
    string | null
  >(null);
  const [activeTab, setActiveTab] = useState<
    "overview" | "graph" | "literature"
  >("overview");
  const [selectedPathway, setSelectedPathway] = useState<string>("");
  const [showGraphView, setShowGraphView] = useState(true);
  const [selectedDisease] = useState<string>("");
  // Snackbar state for download errors
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMsg, setSnackbarMsg] = useState("");
  const [isDrawerExpanded, setIsDrawerExpanded] = useState(false);

  const mechanismDiseaseDisplay = useMemo(() => {
    const param = resolveMechanismDiseaseParam(
      submittedDiseases,
      selectedDisease,
      allowSingleSelection,
    );
    return param ? formatDiseaseListForDisplay(param) : null;
  }, [submittedDiseases, selectedDisease, allowSingleSelection]);

  useEffect(() => {
    const fetchMechanisms = async () => {
      if (!selectedGene) return;

      setStatus(STATUS.loading);
      setErrorMessage(null);
      setSelectedMechanismKey(null); // Reset selection when gene changes
      setSelectedPathway(""); // Reset pathway selection

      try {
        // submittedDiseases is set on Submit; empty means TA-level "All". Single-select sends one label.
        const diseaseParam = resolveMechanismDiseaseParam(
          submittedDiseases,
          selectedDisease,
          allowSingleSelection,
        );
        const result: MechanismsAPIResponse =
          await mechanismsClient.getMechanisms(
            selectedGene,
            therapeuticArea || undefined,
            diseaseParam,
          );

        if (result.status === STATUS.generating) {
          setStatus(STATUS.generating);
          setErrorMessage(
            result.estimated_time !== undefined
              ? `Mechanism report is being generated. Please check back in ${Math.ceil(
                  result.estimated_time / 60 + 5,
                )} minutes.`
              : "Mechanism report is being generated. Please check back later.",
          );
          setMechanisms(null);
          return;
        } else if (result.status === STATUS.queued) {
          setStatus(STATUS.queued);
          setErrorMessage(
            `Your request is queued for processing. Please check back shortly.`,
          );
          setMechanisms(null);
          return;
        } else if (result.status === STATUS.error) {
          setStatus(STATUS.error);
          setErrorMessage(
            "Failed to generate mechanisms report. Please try again later.",
          );
          setMechanisms(null);
          return;
        } else if (result.status === STATUS.not_found) {
          setStatus(STATUS.not_found);
          setErrorMessage(
            "No mechanisms were found for the selected gene and therapeutic area. Please try a different gene symbol, verify your spelling, or broaden your search criteria.",
          );
          setMechanisms(null);
          return;
        } else if (result.status === STATUS.found) {
          if (result.data === null || Object.keys(result.data).length === 0) {
            setStatus(STATUS.error);
            setErrorMessage(
              "No mechanisms were found for the selected gene and therapeutic area. Please try a different gene symbol, verify your spelling, or broaden your search criteria.",
            );
            setMechanisms(null);
            return;
          }
          setStatus(STATUS.found);
          setErrorMessage(null);
          setMechanisms(result.data);
          return;
        }

        // Fallback for unexpected status
        setStatus(STATUS.error);
        setErrorMessage("Unexpected response from server.");
        setMechanisms(null);
      } catch (err) {
        setStatus(STATUS.error);
        setErrorMessage(
          "No mechanisms were found for the selected gene and therapeutic area. Please try a different gene symbol, verify your spelling, or broaden your search criteria.",
        );
        console.error("Error fetching mechanisms:", err);
      }
    };

    fetchMechanisms();
  }, [
    selectedGene,
    therapeuticArea,
    selectedDisease,
    submittedDiseases,
    diseaseRefreshKey,
    allowSingleSelection,
  ]);

  // Pre-select first pathway when mechanism is selected
  useEffect(() => {
    if (selectedMechanismKey && mechanisms?.[selectedMechanismKey]?.paths) {
      const paths = mechanisms[selectedMechanismKey].paths!;
      if (paths.length > 0) {
        setSelectedPathway(paths[0].path);
      }
    } else {
      setSelectedPathway("");
    }
  }, [selectedMechanismKey, mechanisms]);

  // Download Mechanism HTML handler
  const handleDownloadMechanism = async () => {
    try {
      if (!selectedGene || !therapeuticArea) return;
      const diseaseParam = resolveMechanismDiseaseParam(
        submittedDiseases,
        selectedDisease,
        allowSingleSelection,
      );
      const blob = await mechanismsClient.downloadMechanismHtml(
        selectedGene,
        therapeuticArea,
        diseaseParam,
      );
      const link = document.createElement("a");
      link.href = window.URL.createObjectURL(blob);
      link.download = `${selectedGene}_mechanisms.html`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      setSnackbarMsg("Failed to download mechanism.");
      setSnackbarOpen(true);
    }
  };

  if (!selectedGene) {
    return (
      <div className="text-center py-12 text-gray-500">
        <div className="text-6xl mb-4">🧬</div>
        <p>Enter a gene symbol to view detailed information</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <GeneTherapeuticHeader
        selectedGene={selectedGene}
        therapeuticArea={therapeuticArea ?? undefined}
        taxonomyNodes={taxonomyNodes}
        selectedDiseaseIds={selectedDiseaseIds}
        onDiseaseSelectionChange={onDiseaseSelectionChange}
        showDiseaseControls
        onSubmitDiseases={onSubmitDiseases}
        allowSingleSelection={allowSingleSelection}
      />
      {/* <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
        <div className="flex items-center space-x-3 mb-4">
          <div className="w-2 h-6 bg-blue-500 rounded-full"></div>
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
            Enter a disease to fetch Mechanisms
          </h2>
        </div>
        <div className="flex gap-2">
          <div className="flex-1 relative">
            <input
              type="text"
              value={diseaseInputValue}
              onChange={(e) => setDiseaseInputValue(e.target.value)}
              placeholder="Enter disease name (optional)"
              className="w-full px-4 py-3 pl-10 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all shadow-sm"
            />
            <svg
              className="absolute left-3 top-3.5 w-5 h-5 text-gray-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
              />
            </svg>
          </div>
          <button
            onClick={() => setSelectedDisease(diseaseInputValue)}
            className="px-6 py-3 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white font-medium rounded-lg transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 dark:focus:ring-offset-gray-900 shadow-md hover:shadow-lg transform hover:scale-105 flex items-center gap-2"
          >
            Submit
          </button>
        </div>
      </div> */}

      {/* Autocomplete for mechanism selection */}
      <div className="space-y-4">
        {/* Loading State */}
        {status === STATUS.loading && (
          <div className="text-center py-8">
            <div className="animate-spin h-8 w-8 border-4 border-blue-500 border-t-transparent rounded-full mx-auto"></div>
            <p className="mt-4 text-gray-600 dark:text-gray-400">
              Loading mechanisms
              {mechanismDiseaseDisplay ? (
                <>
                  {" "}
                  for{" "}
                  <span className="font-medium text-gray-800 dark:text-gray-200">
                    {mechanismDiseaseDisplay}
                  </span>
                </>
              ) : null}
              …
            </p>
          </div>
        )}

        {/* Queued State */}
        {status === STATUS.queued && (
          <div className="text-center py-8">
            <div className="text-4xl mb-4">📋</div>
            <p className="text-blue-600 dark:text-blue-400 font-medium">
              {errorMessage}
            </p>
          </div>
        )}

        {/* Generating State */}
        {status === STATUS.generating && (
          <div className="text-center py-8">
            <div className="text-4xl mb-4">⏳</div>
            <p className="text-yellow-600 dark:text-yellow-400 font-medium">
              {errorMessage}
            </p>
          </div>
        )}

        {/* Error State */}
        {status === STATUS.error && (
          <div className="text-center py-8 text-red-500">
            <p className="text-sm font-medium">{errorMessage}</p>
          </div>
        )}

        {/* Not Found State */}
        {status === STATUS.not_found && (
          <div className="text-center py-8">
            <div className="text-4xl mb-4">🔍</div>
            <p className="text-gray-500 dark:text-gray-400">
              {errorMessage ||
                "No mechanisms found for the specified criteria."}
            </p>
          </div>
        )}

        {/* Mechanism Selection - Only show when status is found and has data */}
        {status === STATUS.found &&
          mechanisms &&
          Object.keys(mechanisms).length > 0 && (
            <>
              <div className="flex justify-end mb-2">
                <button
                  onClick={handleDownloadMechanism}
                  className="inline-flex items-center px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg shadow transition-colors duration-200"
                  title="Download Mechanisms"
                >
                  <svg
                    className="w-4 h-4 mr-2"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M4 16v2a2 2 0 002 2h12a2 2 0 002-2v-2M7 10l5 5m0 0l5-5m-5 5V4"
                    />
                  </svg>
                  Download Mechanisms
                </button>
              </div>
              <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 min-w-0 max-w-full">
                {/* Left Panel - Mechanism List */}
                <div
                  className={`transition-all duration-300 space-y-3 min-w-0 ${
                    isDrawerExpanded ? "hidden" : "lg:col-span-3"
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <label className="text-sm font-medium">
                      Available Mechanisms
                    </label>
                    <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded-full">
                      {Object.keys(mechanisms).length}
                    </span>
                  </div>

                  {/* Mechanism List */}
                  <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg divide-y divide-gray-200 dark:divide-gray-700 max-h-[800px] overflow-y-auto">
                    {Object.entries(mechanisms)
                      .sort(([, a], [, b]) => a.name.localeCompare(b.name))
                      .map(([key, mechanism]) => (
                        <button
                          key={key}
                          onClick={() => {
                            setSelectedMechanismKey(key);
                            setActiveTab("overview");
                            setShowGraphView(true);
                          }}
                          className={`w-full text-left px-4 py-3 transition-colors ${
                            selectedMechanismKey === key
                              ? "bg-blue-50 dark:bg-blue-950 text-blue-700 dark:text-blue-300 font-medium"
                              : "text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800"
                          }`}
                        >
                          <div className="flex items-center justify-between">
                            <span className="text-sm">{mechanism.name}</span>
                            {selectedMechanismKey === key && (
                              <span className="text-blue-600 dark:text-blue-300">
                                ✓
                              </span>
                            )}
                          </div>
                        </button>
                      ))}
                  </div>
                </div>

                {/* Right Panel - Mechanism Details */}
                <div
                  className={`transition-all duration-300 min-w-0 max-w-full ${
                    isDrawerExpanded ? "lg:col-span-12" : "lg:col-span-9"
                  }`}
                >
                  {selectedMechanismKey &&
                  mechanisms?.[selectedMechanismKey] ? (
                    <div className="bg-white dark:bg-gray-900 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 min-w-0 max-w-full overflow-x-hidden">
                      {/* Header with mechanism name and rating */}
                      <div className="flex justify-between items-center p-6 border-b border-gray-200 dark:border-gray-700">
                        {/* Drawer Toggle Button */}
                        <button
                          onClick={() => setIsDrawerExpanded(!isDrawerExpanded)}
                          className="mr-3 p-2 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
                          title={
                            isDrawerExpanded
                              ? "Show mechanism list"
                              : "Expand to full width"
                          }
                        >
                          <svg
                            className="w-5 h-5"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            {isDrawerExpanded ? (
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M4 6h16M4 12h16M4 18h16"
                              />
                            ) : (
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4"
                              />
                            )}
                          </svg>
                        </button>
                        <div className="flex-1 flex justify-between items-center">
                          <h5 className="text-xl font-semibold text-gray-900 dark:text-white">
                            {mechanisms[selectedMechanismKey].name}
                          </h5>
                          <span className="px-3 py-1 bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 text-sm font-medium rounded-full">
                            Rating: {mechanisms[selectedMechanismKey].rating}
                          </span>
                        </div>
                      </div>

                      {/* Tabs */}
                      <div className="border-b border-gray-200 dark:border-gray-700">
                        <div className="flex gap-1 px-6">
                          <button
                            onClick={() => setActiveTab("overview")}
                            className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                              activeTab === "overview"
                                ? "border-blue-600 text-blue-600 dark:border-blue-400 dark:text-blue-400"
                                : "border-transparent text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white hover:border-gray-300 dark:hover:border-gray-600"
                            }`}
                          >
                            📊 Overview
                          </button>
                          <button
                            onClick={() => setActiveTab("graph")}
                            className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                              activeTab === "graph"
                                ? "border-blue-600 text-blue-600 dark:border-blue-400 dark:text-blue-400"
                                : "border-transparent text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white hover:border-gray-300 dark:hover:border-gray-600"
                            }`}
                          >
                            🔗 Graph Evidence
                          </button>
                          <button
                            onClick={() => setActiveTab("literature")}
                            className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                              activeTab === "literature"
                                ? "border-blue-600 text-blue-600 dark:border-blue-400 dark:text-blue-400"
                                : "border-transparent text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white hover:border-gray-300 dark:hover:border-gray-600"
                            }`}
                          >
                            📚 Literature
                          </button>
                        </div>
                      </div>

                      {/* Tab Content */}
                      <div className="p-6 dark:bg-gray-900 min-w-0 max-w-full">
                        {/* Overview Tab */}
                        {activeTab === "overview" && (
                          <div className="space-y-6">
                            {/* Summary Section */}
                            <div>
                              <h6 className="text-base font-semibold text-gray-700 dark:text-gray-200 mb-2">
                                Summary
                              </h6>
                              <div className="bg-blue-50 dark:bg-blue-950 border-l-4 border-blue-400 dark:border-blue-700 p-4 rounded">
                                <p className="text-gray-700 dark:text-gray-200 leading-relaxed">
                                  {
                                    mechanisms[selectedMechanismKey]
                                      .short_summary
                                  }
                                </p>
                              </div>
                            </div>

                            {/* Relevant Genes Section */}
                            {mechanisms[selectedMechanismKey].genes_in_paths &&
                              mechanisms[selectedMechanismKey].genes_in_paths!
                                .length > 0 && (
                                <div>
                                  <h6 className="text-base font-semibold text-gray-700 dark:text-gray-200 mb-2">
                                    Relevant Genes
                                  </h6>
                                  <div className="flex flex-wrap gap-2">
                                    {mechanisms[
                                      selectedMechanismKey
                                    ].genes_in_paths!.map((gene, index) => (
                                      <span
                                        className="px-3 py-1 bg-indigo-100 dark:bg-indigo-900 text-indigo-800 dark:text-indigo-200 text-sm font-medium rounded-full"
                                        key={index}
                                      >
                                        {gene}
                                      </span>
                                    ))}
                                  </div>
                                </div>
                              )}

                            <hr />

                            {/* Pathway Details Section */}
                            {mechanisms[selectedMechanismKey].paths &&
                              mechanisms[selectedMechanismKey].paths!.length >
                                0 && (
                                <div className="space-y-4">
                                  <h6 className="text-base font-semibold text-gray-700 dark:text-gray-200 mb-2">
                                    Mechanism Details
                                  </h6>

                                  <FormControl
                                    fullWidth
                                    size="small"
                                    variant="outlined"
                                    sx={{
                                      backgroundColor:
                                        themeCtx.mode === "dark"
                                          ? "#1a202c"
                                          : "#fff",
                                      borderRadius: 1,
                                      "& .MuiInputLabel-root": {
                                        color:
                                          themeCtx.mode === "dark"
                                            ? "#cbd5e1"
                                            : undefined,
                                      },
                                      "& .MuiOutlinedInput-root": {
                                        backgroundColor:
                                          themeCtx.mode === "dark"
                                            ? "#1a202c"
                                            : "#fff",
                                        color:
                                          themeCtx.mode === "dark"
                                            ? "#e5e7eb"
                                            : "#111827",
                                        "& fieldset": {
                                          borderColor:
                                            themeCtx.mode === "dark"
                                              ? "#334155"
                                              : "#cbd5e1",
                                        },
                                      },
                                      "& .MuiSelect-icon": {
                                        color:
                                          themeCtx.mode === "dark"
                                            ? "#cbd5e1"
                                            : undefined,
                                      },
                                    }}
                                  >
                                    <InputLabel
                                      id="pathway-select-label"
                                      className="dark:text-gray-300"
                                    >
                                      Select a pathway to explore
                                    </InputLabel>
                                    <Select
                                      labelId="pathway-select-label"
                                      value={selectedPathway}
                                      onChange={(e) =>
                                        setSelectedPathway(e.target.value)
                                      }
                                      displayEmpty
                                      label="Select a pathway to explore"
                                      className="bg-white dark:bg-gray-900 dark:text-gray-100"
                                      MenuProps={{
                                        PaperProps: {
                                          sx: {
                                            backgroundColor:
                                              themeCtx.mode === "dark"
                                                ? "#1a202c"
                                                : "#fff",
                                            color:
                                              themeCtx.mode === "dark"
                                                ? "#e5e7eb"
                                                : "#111827",
                                            border:
                                              themeCtx.mode === "dark"
                                                ? "1px solid #334155"
                                                : "1px solid #cbd5e1",
                                            "& .MuiMenuItem-root": {
                                              backgroundColor:
                                                themeCtx.mode === "dark"
                                                  ? "#1a202c"
                                                  : "#fff",
                                              color:
                                                themeCtx.mode === "dark"
                                                  ? "#e5e7eb"
                                                  : "#111827",
                                              "&.Mui-selected": {
                                                backgroundColor:
                                                  themeCtx.mode === "dark"
                                                    ? "#334155"
                                                    : "#e5e7eb",
                                                color:
                                                  themeCtx.mode === "dark"
                                                    ? "#fff"
                                                    : "#111827",
                                              },
                                              "&:hover": {
                                                backgroundColor:
                                                  themeCtx.mode === "dark"
                                                    ? "#334155"
                                                    : "#f3f4f6",
                                                color:
                                                  themeCtx.mode === "dark"
                                                    ? "#fff"
                                                    : "#111827",
                                              },
                                            },
                                          },
                                        },
                                      }}
                                    >
                                      <MenuItem value="" disabled>
                                        Select a pathway
                                      </MenuItem>
                                      {mechanisms[
                                        selectedMechanismKey
                                      ].paths!.map((pathObj, index) => (
                                        <MenuItem
                                          key={index}
                                          value={pathObj.path}
                                        >
                                          {pathObj.path}
                                        </MenuItem>
                                      ))}
                                    </Select>
                                  </FormControl>

                                  {selectedPathway &&
                                    mechanisms[
                                      selectedMechanismKey
                                    ].paths!.find(
                                      (p) => p.path === selectedPathway,
                                    ) && (
                                      <div className="bg-blue-50 dark:bg-blue-950 border-l-4 border-blue-400 dark:border-blue-700 p-4 rounded">
                                        <p className="text-gray-700 dark:text-gray-200 leading-relaxed">
                                          {
                                            mechanisms[
                                              selectedMechanismKey
                                            ].paths!.find(
                                              (p) => p.path === selectedPathway,
                                            )!.functional_summary
                                          }
                                        </p>
                                      </div>
                                    )}

                                  {/* Graph / Table segmented control (default: Graph), aligned like DirectNeighborGraph */}
                                  {selectedPathway && (
                                    <div className="space-y-4 w-full min-w-0 max-w-full">
                                      <div className="flex w-full justify-end">
                                        <div className="flex items-center gap-2">
                                          <span className="text-sm text-gray-600 dark:text-gray-400">
                                            View:
                                          </span>
                                          <div className="flex rounded-lg border border-gray-200 dark:border-slate-600 overflow-hidden bg-gray-50 dark:bg-slate-700">
                                            <button
                                              type="button"
                                              onClick={() => setShowGraphView(true)}
                                              className={`px-3 py-1.5 text-sm font-medium transition-colors ${
                                                showGraphView
                                                  ? "bg-white dark:bg-slate-600 text-gray-900 dark:text-gray-100 shadow-sm"
                                                  : "text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200"
                                              }`}
                                            >
                                              Graph
                                            </button>
                                            <button
                                              type="button"
                                              onClick={() => setShowGraphView(false)}
                                              className={`px-3 py-1.5 text-sm font-medium transition-colors border-l border-gray-200 dark:border-slate-600 ${
                                                !showGraphView
                                                  ? "bg-white dark:bg-slate-600 text-gray-900 dark:text-gray-100 shadow-sm"
                                                  : "text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200"
                                              }`}
                                            >
                                              Table
                                            </button>
                                          </div>
                                        </div>
                                      </div>

                                      {showGraphView ? (
                                        <PathwayGraph
                                          pathData={mechanisms[
                                            selectedMechanismKey
                                          ].paths!.find(
                                            (p) => p.path === selectedPathway,
                                          )}
                                        />
                                      ) : (
                                        <PathwayTable
                                          selectedPathway={selectedPathway}
                                          pathData={mechanisms[
                                            selectedMechanismKey
                                          ].paths!.find(
                                            (p) => p.path === selectedPathway,
                                          )}
                                        />
                                      )}
                                    </div>
                                  )}
                                </div>
                              )}
                          </div>
                        )}

                        {/* Graph Evidence Tab */}
                        {activeTab === "graph" && (
                          <GraphEvidenceTab
                            mechanism={mechanisms[selectedMechanismKey]}
                          />
                        )}

                        {/* Literature Tab */}
                        {activeTab === "literature" && (
                          <LiteratureTab
                            mechanism={mechanisms[selectedMechanismKey]}
                            gene={selectedGene}
                          />
                        )}
                      </div>
                    </div>
                  ) : (
                    <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg text-center h-full flex items-start justify-center">
                      <div className="max-w-md pt-12 pb-4">
                        <div className="w-20 h-20 mx-auto mb-6 bg-blue-50 dark:bg-blue-950 rounded-full flex items-center justify-center">
                          <svg
                            className="w-10 h-10 text-blue-400 dark:text-blue-300"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={1.5}
                              d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                            />
                          </svg>
                        </div>
                        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                          No Mechanism Selected
                        </h3>
                        <p className="text-sm text-gray-600 dark:text-gray-300 leading-relaxed">
                          Choose a mechanism from the list on the left to view
                          detailed information, including pathways, graph
                          evidence, and supporting literature.
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </>
          )}
      </div>
      {/* Snackbar for download errors */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={() => setSnackbarOpen(false)}
          severity="error"
          sx={{ width: "100%" }}
        >
          {snackbarMsg}
        </Alert>
      </Snackbar>
    </div>
  );
};
// ...existing code...
